import React, { useEffect, useState, useRef } from 'react';
import PropertyBase, { IBasePropertyProps } from './PropertyBase';
import Engine from '../../classes/Engine';
import { TooltipKeys } from '../tooltips/TooltipKeys';
import MathUtils from '../../classes/utilities/MathUtils';
import useForceUpdate from '../reactUtils/useForceUpdate';
import AnalyticsManager from '../../classes/AnalyticsManager';

interface INumberPropertyProps extends Omit<IBasePropertyProps, 'onChangeCallback' | 'onCommitCallback'>
{
	/** if true, the internal input field will gain focus as soon as it's drawn */
	autoFocus?: boolean;
	/** the minimum value of the internal input field */
	min?: number;
	/** the maximum value the internal input field */
	max?: number;
	/** the increment value used by the internal input field when scrolling with the mouse or using the inline arrows. *defaults to 1* */
	step?: number;
	/** determines whether or not the input field's value should be displayed while taking world units into account (usually true for 3D-related properties) */
	unitScaling?: boolean;
	/** determines if the fieldn't value can be changed by dragging from its label with the mouse */
	disableDrag?: boolean;
	/** used when changing the field's value by dragging from its label. *defaults to 1* */
	sensitivity?: number;
	/** an optional react component that will be added to the input field itself, for use in properties that have multiple number sub-properties */
	compositeLabel?: React.ReactNode;
	/** an HTML className to add to the compositeLabel component, e.g. for styling or automation purposes */
	compositeLabelClass?: string;
	onChangeCallback?: (newValue: number, e: React.ChangeEvent<HTMLInputElement>) => void;
	onCommitCallback?: (newValue: number, compositeLabel: React.ReactNode) => void;
	/** if true, hides the input field's increment/decrement arrows  */
	hideArrows?: boolean;
	/** define a string that will be used to split the number every 3 digits. for example: 10,000,000 */
	splitbignumbers?: string;
	/** defines the amount of decimal digits to display in the input field. *defaults to 2* */
	decimals?: number;
	placeholder?: number;
	postfixLabel?: React.ReactNode;
	/** callback to execute when the input field gains focus */
	onFocus?: (field: HTMLInputElement) => void;
	/** callback to execute when the input field loses focus */
	onBlur?: (field: HTMLInputElement) => void;
	/** additional CSS class used by the base property and applied to the table cell that contains this property */
	cellclass?: string;
}

/**
 * defines an input field for numeric values, that can be set to read-only or be edited by direct input or - for composite fields - by dragging from the field's composite label
 *
 * @export
 * @param {(INumberPropertyProps)} props
 * @return {*} 
 */
export default function NumberProperty(props: INumberPropertyProps)
{
	const forceUpdate = useForceUpdate();
	
	const [isDragging, setIsDragging] = useState(false);
	const [isFocused, setIsFocused] = useState(false);
	const [preventBlur, setPreventBlur] = useState(false);
	const [isMouseOver, setIsMouseOver] = useState(false);
	
	const fieldRef = useRef<HTMLInputElement>(null);
	const formatValue = (value: number | undefined | null, splitChar?: string): string => 
	{
		if (value === undefined || value === null || Number.isNaN(value)) 
		{
			return "0";
		}
		return splitChar ? MathUtils.formatBigNumber(value, splitChar) : String(value);
	};

	const tempValue = useRef(props.multivalues 
		? "~" 
		: formatValue(props.value, props.splitbignumbers));

	const dataName = (props["data-name"] ? props["data-name"] + "-" : "") + "-number-property";
	const allowedKeys = props.splitbignumbers ? "0123456789" : "0123456789.-";
	const decimalCount = props.decimals || props.decimals === 0 ? props.decimals : 2;
	const sensitivity = props.sensitivity ?? 1;
	const step = props.step ?? 1;

	useEffect(() =>
	{
		if (fieldRef.current && props.autoFocus)
		{
			fieldRef.current.focus();
			setIsFocused(true);
		}
	}, [props.autoFocus]);

	useEffect(() =>
	{
		if (props.multivalues) 
		{
			tempValue.current = "~";
		}
		else 
		{
			tempValue.current = formatValue(props.value, props.splitbignumbers);
		}
		forceUpdate();
	}, [props.value, props.multivalues, props.splitbignumbers]);

	useEffect(() =>
	{
		if (isFocused && fieldRef.current)
		{
			// thanks to https://stackoverflow.com/a/55803519/618726
			fieldRef.current.type = 'text';
			fieldRef.current.setSelectionRange(0, fieldRef.current.value.length);
			fieldRef.current.type = 'number';
			fieldRef.current.focus();
		}
	}, [isFocused, fieldRef.current]);

	useEffect(() =>
	{
		if (isDragging)
		{
			window.addEventListener('mousemove', dragValue);
			window.addEventListener('mouseup', stopDrag);
		}
		else
		{
			window.removeEventListener('mousemove', dragValue);
			window.removeEventListener('mouseup', stopDrag);
		}
	}, [isDragging]);

	const cleanNumber = (value: string) => 
	{
		const splitChar = props.splitbignumbers || "";
		if (!splitChar) return value;
		return value.replace(new RegExp(splitChar, 'g'), '');
	};

	function beginDrag(e)
	{
		if (props.disableDrag || props.readOnly) return;

		e = e || window.event;
		e.preventDefault();

		setIsDragging(true);
	}

	function dragValue(e)
	{
		e = e || window.event;
		e.preventDefault();

		let dragScale = sensitivity;
		if (e.ctrlKey || e.metaKey)
		{
			dragScale *= 3;

			if (e.altKey)
			{
				dragScale *= 10;
			}
		}
		else if (e.shiftKey)
		{
			dragScale *= 0.5;
		}
		else if (e.altKey)
		{
			dragScale *= 0.1;
		}

		let newValue = (Number(fieldRef.current?.value || 0) + ((/*props.unitScaling ? step / Engine.getInstance().getDisplayUnitScale() :*/ step) * dragScale * Math.sign(e.movementX)));
		if (isNaN(newValue))
		{
			newValue = props.value || props.min || props.placeholder || 0;
		}

		tempValue.current = String(newValue);
		forceUpdate();
		callOnChange(null);
	}

	function stopDrag(e)
	{
		e = e || window.event;
		e.preventDefault();

		if (props['data-name'])
		{
			AnalyticsManager.managedEvent('propertyChanged', 'propertyId', props['data-name']);
		}

		setIsDragging(false);
		if (props.onCommitCallback)
		{
			const val = parseFloat(tempValue.current) * (props.unitScaling ? Engine.getInstance().getDisplayUnitScale() : 1);
			props.onCommitCallback(val, props.compositeLabel);
		}

		window.removeEventListener('mousemove', dragValue);
		window.removeEventListener('mouseup', stopDrag);
	}

	function callOnChange(e)
	{
		if (!isNaN(parseFloat(tempValue.current)) && isFinite(parseFloat(cleanNumber(tempValue.current))))
		{
			const fUnitScale = Engine.getInstance().getDisplayUnitScale();
			let value = parseFloat(tempValue.current);
			if (props.unitScaling) { value = value * fUnitScale; }
			const minVal = props.min !== undefined ? props.min : value;
			const maxVal = props.max !== undefined ? props.max : value;

			if (minVal <= value && maxVal >= value)  // WC20-1847 : Let the user type any digits until the final value is set and submitted, but avoid setting the property to an invalid value...
			{
				if (e)
				{
					AnalyticsManager.managedEvent('propertyChanged', 'propertyId', e.target.dataset.name);
				}

				if (props.onChangeCallback)
				{
					props.onChangeCallback(Number(value), e);
				}
			}
		}
	}

	let faceValue = tempValue.current;
	if (!isFocused && !props.multivalues)
	{
		const numValue = Number(cleanNumber(tempValue.current));
		if (!Number.isNaN(numValue)) 
		{
			faceValue = formatValue(numValue, props.splitbignumbers);
		}
	}

	return (
		<PropertyBase 
			{...Object.assign({}, props, {
				onChangeCallback: (value: any) => props.onChangeCallback?.(value, null as any),
				onCommitCallback: (value: any) => props.onCommitCallback?.(value, null as any)
			})}
			data-name={dataName + "-root"}
			//className={"numberDrag"} <- add to label somehow
			//onMouseDown={beginDrag} <- add to label somehow
		>
			<div className={"inputRoot" + (props.postfixLabel ? " narrow" : "")}
				onMouseEnter={(e) =>
				{
					if (!isMouseOver)
						setIsMouseOver(true);
				}}
				onMouseLeave={(e) =>
				{
					if (isMouseOver)
						setIsMouseOver(false);
				}}
			>
				{
					!props.compositeLabel ? null :
						<div className={"compositeLabel numberDrag " + props.compositeLabelClass}
							data-readonly={props.readOnly ? "true" : "false"}
							onMouseDown={beginDrag}
							data-name={dataName + "-input-drag-handle"}
							data-tooltip={TooltipKeys.NumberPropertyDragLabelInteractions}
						>
							{props.compositeLabel}
						</div>
				}
				<input className="textField"
					data-name={dataName + "-input"}
					ref={fieldRef}
					type={props.multivalues || props.splitbignumbers ? "" : "number"}
					placeholder="~"
					value={faceValue}
					step={/*props.unitScaling ? step / Engine.getInstance().getDisplayUnitScale() :*/ step}
					readOnly={props.readOnly ? props.readOnly : false}
					onChange={(e) =>
					{
						setIsFocused(true);
						tempValue.current = cleanNumber(e.target.value);
						forceUpdate();
						callOnChange(e);
					}}
					onFocus={() =>
					{
						tempValue.current = cleanNumber(tempValue.current);
						fieldRef.current?.select();
						setIsFocused(true);
						if(fieldRef.current) 
						{
							props.onFocus?.(fieldRef.current);
						}
					}}
					onBlur={(e) =>
					{
						if (!preventBlur)
						{
							setIsFocused(false);
							if(fieldRef.current) 
							{
								props.onBlur?.(fieldRef.current);
							}
						}
						else
						{
							setPreventBlur(false);
						}

						if (!props.readOnly && props.onCommitCallback)
						{
							let bCallOnchange = false;
							if (isNaN(parseFloat(tempValue.current)))
							{
								if ('' === tempValue.current)
								{
									tempValue.current = props.defaultValue ?? (props.value ?? (props.min ?? 0));
								}
								bCallOnchange = true;
							}

							//If it's still not a legible number, e.g. some textual placeholder, skip the update
							if (isNaN(parseFloat(tempValue.current)))
							{
								return;
							}

							const fUnitScale = Engine.getInstance().getDisplayUnitScale();
							const val = parseFloat(tempValue.current) * (props.unitScaling ? fUnitScale : 1);

							if (props.max !== undefined && props.max < val)
							{
								tempValue.current = String(props.unitScaling ? props.max / fUnitScale : props.max);
								bCallOnchange = true;
							}
							if (props.min !== undefined && props.min > val)
							{
								tempValue.current = String(props.unitScaling ? props.min / fUnitScale : props.min);
								bCallOnchange = true;
							}

							AnalyticsManager.managedEvent('propertyChanged', 'propertyId', e.target.dataset.name);

							//If min/max was enforced here, first call onChange
							const fNormalizedInput = parseFloat(cleanNumber(tempValue.current)) * (props.unitScaling ? fUnitScale : 1);
							if (bCallOnchange || val != fNormalizedInput)
							{
								if (props.onChangeCallback)
								{
									props.onChangeCallback(fNormalizedInput, e);
								}
							}

							//console.log(fNormalizedInput);
							props.onCommitCallback(fNormalizedInput, props.compositeLabel);
						}

						tempValue.current = MathUtils.formatBigNumber(parseFloat(cleanNumber(tempValue.current)), props.splitbignumbers);
					}}
					onKeyPress={(e) =>
					{
						if (e.key === "Enter")
						{
							(e.target as HTMLInputElement).blur();
						}
						else if (!allowedKeys.includes(e.key))
						{
							e.preventDefault();
						}
					}}
					onWheel={(e) =>
					{
						if(fieldRef.current)
						{
							fieldRef.current.value = String(Number(fieldRef.current.value) + Number(props.step) * Math.sign(-e.nativeEvent.deltaY));
						}
					}}
				/>
			</div>
			{
				props.postfixLabel ?
					<div className="narrow postfixLabel"> {props.postfixLabel} </div>
					: null
			}
		</PropertyBase>
	);
}
